import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, FileBarChart, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { useUserProfile } from '@/features/auth/api';
import { BULK_REPORT_MODULE_ID } from '@/features/users/components/module-meta';
import { formatDate } from '@/lib/utils/date';
import { useCampaignReportDownload, useGenerateCampaignReport, useHistoricalCampaigns } from '../api';
import { buildCampaignColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import {
  CampaignFilters,
  applyCampaignFilters,
  rowsHaveRetries,
} from '../components/CampaignFilters';
import type { CampaignRow } from '../types';

const BULK_REPORT_OPTIONS = [
  { value: 'bulk',           label: 'Generate Bulk Report' },
  { value: 'dtmf_bulk',      label: 'Generate DTMF Bulk Report' },
  { value: 'connected_bulk', label: 'Generate Connected Bulk Report' },
] as const;

type BulkReportType = (typeof BULK_REPORT_OPTIONS)[number]['value'];

export function VoiceCampaignHistoricalPage() {
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const profile = useUserProfile();
  // Superadmin/sub-superadmin/reseller always see bulk report; end-users need module 14
  const isBulkReportAllowed = role !== 'ROLE_USER' ||
    (profile.data?.modules ?? []).some((m) => String(m.moduleId) === BULK_REPORT_MODULE_ID);
  const search = useHistoricalCampaigns();
  const allRows = search.data ?? [];

  const [startDate, setStartDate] = useState<Date | undefined>(
    () => { const d = new Date(); d.setDate(d.getDate() - 7); return d; }
  );
  const [endDate, setEndDate] = useState<Date | undefined>(() => new Date());
  const [_hasSearched, setHasSearched] = useState(false);

  const [selectedBulkType, setSelectedBulkType] = useState<BulkReportType>('bulk');
  const [bulkDropdownOpen, setBulkDropdownOpen] = useState(false);

  const selectedBulkLabel =
    BULK_REPORT_OPTIONS.find((o) => o.value === selectedBulkType)?.label ??
    BULK_REPORT_OPTIONS[0].label;

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);

  useEffect(() => {
  if (!userId) return;

  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);

  search.mutate({
    userId: Number(userId),
    startDate: formatDate(start),
    endDate: formatDate(end),
  });

  setHasSearched(true);
}, [userId]);

  const rows = useMemo(
    () => applyCampaignFilters(allRows, selectedStatuses, selectedTemplates),
    [allRows, selectedStatuses, selectedTemplates],
  );

  const hasRetries = rowsHaveRetries(rows);
  const baseTotal = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.numbersUploaded ?? 0), 0),
    [rows],
  );

  // Historical campaigns still need the row-actions menu — users open
  // it to generate full / webhook reports, retry the campaign, or fire
  // webhook-retry actions. Each menu item is internally status-gated.
  const reportDownload = useCampaignReportDownload();
  const bulkGenerate = useGenerateCampaignReport();

  const DTMF_TEMPLATE_IDS = [1, 8]; // 1 = DTMF, 8 = TTS DTMF

  const handleBulkReport = async (ids: string[], clear: () => void) => {
    if (ids.length > 100) {
      toast.error('Maximum 100 campaigns allowed for Bulk Report. Please reduce your selection.', { duration: 5000 });
      return;
    }
    const selectedRows = rows.filter((r) => ids.includes(String(r.campaignId)));

    // DTMF validation
    if (selectedBulkType === 'dtmf_bulk') {
      const nonDtmf = selectedRows.filter(
        (r) => !DTMF_TEMPLATE_IDS.includes(r.templateId),
      );
      if (nonDtmf.length > 0) {
        const names = nonDtmf.map((r) => r.campaignName).join(', ');
        toast.error(
          `These campaigns are not DTMF — please remove them: ${names}. Only DTMF campaigns can be downloaded.`,
          { duration: 6000 },
        );
        return;
      }
    }

    // Connected validation
    let finalIds = ids;
    if (selectedBulkType === 'connected_bulk') {
      const validRows = selectedRows.filter(
        (r) => (r.callsConnected ?? 0) > 0,
      );
      const skipped = selectedRows.length - validRows.length;
      if (validRows.length === 0) {
        toast.error('None of the selected campaigns have connected calls.', {
          duration: 5000,
        });
        return;
      }
      if (skipped > 0) {
        toast.info(
          `${skipped} campaign(s) skipped (0 connected calls).`,
          { duration: 4000 },
        );
      }
      finalIds = validRows.map((r) => String(r.campaignId));
    }

    try {
      await bulkGenerate.mutateAsync({
        campaignId: 0,
        reportType: selectedBulkType,
        campaignIds: finalIds.join(','),
      });
      toast.success(
        'Report Generation is in process, Please download the report from Reports Section',
        { duration: 5000 },
      );
    } catch {
      toast.error('Report generation failed. Please try again.');
    }
    clear();
  };
  const columns = useMemo(
    () =>
      buildCampaignColumns({
        onDownloadReport: reportDownload.download,
        baseTotal,
      }),
    [reportDownload.download, baseTotal],
  );

  const visibility = {
    ...DEFAULT_VISIBLE_COLUMNS,
    retryCount: hasRetries,
    retries: hasRetries,
  };

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    setHasSearched(true);
    search.mutate(
      {
        userId: Number(userId),
        startDate: startDate ? formatDate(startDate) : '',
        endDate: endDate ? formatDate(endDate) : '',
      },
      {
        onError: () => toast.error('Could not fetch historical campaigns.'),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Campaign History"
        description="Look up completed campaigns by date range."
      />

      {/*
        Inline filter strip — the old Card-wrapped variant burned a lot of
        vertical space above an otherwise-empty table. Single row with a
        muted hint on the right keeps the date inputs visible without
        crowding the page.
      */}
      <div className="flex flex-col gap-2 rounded-lg border bg-card p-3 sm:flex-row sm:items-center">
        <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-center">
          <DatePicker
            value={startDate}
            onChange={setStartDate}
            placeholder="Start date"
          />
          <DatePicker
            value={endDate}
            onChange={setEndDate}
            placeholder="End date"
          />
          <Button onClick={handleSearch} disabled={search.isPending}>
            {search.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Search
          </Button>
        </div>
        <span className="text-[11px] text-muted-foreground sm:ml-3 sm:whitespace-nowrap">
          Defaults to last 7 days. Change dates to view older campaigns.
        </span>
      </div>

            <DataTable<CampaignRow, unknown>
        tableId="campaigns:voice:historical:v1"
        columns={columns}
        data={rows}
        isLoading={search.isPending}
        searchPlaceholder="Filter results…"
        emptyTitle="No historical campaigns found"
        emptyDescription="No historical campaigns are available."
        enableRowSelection={isBulkReportAllowed}
        getRowId={(row) => String(row.campaignId)}
        initialColumnVisibility={visibility}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="voice-campaigns-historical.csv"
        pageSize={10}
        renderSelectionActions={(ids, clear) => (
          <div className="flex items-center gap-2">
            {/* Dropdown — pick bulk report type */}
            <div className="relative">
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() => setBulkDropdownOpen((o) => !o)}
              >
                {selectedBulkLabel}
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>

              {bulkDropdownOpen && (
                <div className="absolute left-0 top-full z-50 mt-1 min-w-[250px] rounded-md border bg-popover p-1 shadow-md">
                  {BULK_REPORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      className={`w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground ${
                        selectedBulkType === opt.value
                          ? 'bg-accent text-accent-foreground font-medium'
                          : ''
                      }`}
                      onClick={() => {
                        setSelectedBulkType(opt.value);
                        setBulkDropdownOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button
              size="sm"
              onClick={() => handleBulkReport(ids, clear)}
              disabled={ids.length === 0 || bulkGenerate.isPending}
            >
              {bulkGenerate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileBarChart className="h-4 w-4" />
              )}
              {selectedBulkLabel} ({ids.length})
            </Button>
          </div>
        )}
        toolbar={
          <CampaignFilters
            selectedStatuses={selectedStatuses}
            selectedTemplates={selectedTemplates}
            onStatusChange={setSelectedStatuses}
            onTemplateChange={setSelectedTemplates}
          />
        }
      />
    </FillPage>
  );
}
